import React, { useState, useEffect } from 'react';

const FrequentTravelerTab = ({
  programs = [],
  onUpdate,
  executiveId
}) => {
  const [frequentTravelerPrograms, setFrequentTravelerPrograms] = useState(programs);
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentProgram, setCurrentProgram] = useState({
    program_type: 'airline',
    program_name: '',
    membership_number: '',
    tier_level: '',
    notes: ''
  });
  const [error, setError] = useState('');

  const programTypeLabels = {
    airline: 'Airline',
    hotel: 'Hotel',
    rental_car: 'Rental Car',
    other: 'Other'
  };

  const programTypeIcons = {
    airline: '✈️',
    hotel: '🏨',
    rental_car: '🚗',
    other: '🎁'
  };

  // Update programs when props change
  useEffect(() => {
    setFrequentTravelerPrograms(programs);
  }, [programs]);

  const handleSaveProgram = () => {
    if (!currentProgram.program_name || !currentProgram.membership_number) {
      setError('Program name and membership number are required');
      return;
    }

    let updatedPrograms;
    if (editingIndex !== null) {
      updatedPrograms = [...frequentTravelerPrograms];
      updatedPrograms[editingIndex] = currentProgram;
    } else {
      updatedPrograms = [...frequentTravelerPrograms, currentProgram];
    }

    setFrequentTravelerPrograms(updatedPrograms);
    onUpdate(updatedPrograms);
    handleCancelEdit();
  };

  const handleEditProgram = (program, index) => {
    setCurrentProgram({
      program_type: program.program_type || 'other',
      program_name: program.program_name || program.company_name || '',
      membership_number: program.membership_number || program.member_number || '',
      tier_level: program.tier_level || '',
      notes: program.notes || ''
    });
    setEditingIndex(index);
    setShowForm(true);
    setError('');
  };

  const handleRemoveProgram = (index) => {
    const updatedPrograms = frequentTravelerPrograms.filter((_, i) => i !== index);
    setFrequentTravelerPrograms(updatedPrograms);
    onUpdate(updatedPrograms);
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingIndex(null);
    setCurrentProgram({
      program_type: 'airline',
      program_name: '',
      membership_number: '',
      tier_level: '',
      notes: ''
    });
    setError('');
  };

  return (
    <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl shadow-slate-200/50 rounded-3xl p-8 overflow-hidden relative">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50/40 via-transparent to-orange-50/40 pointer-events-none" />

      {/* Header */}
      <div className="relative flex items-center gap-4 mb-8">
        <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Loyalty Programs</h2>
          <p className="text-slate-500 mt-1">Frequent flyer, hotel, and rental car memberships</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="relative mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="relative space-y-6">
        {/* Programs List */}
        {frequentTravelerPrograms.length > 0 && (
          <div className="space-y-3">
            {frequentTravelerPrograms.map((program, index) => (
              <div key={`program-${index}`} className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{programTypeIcons[program.program_type] || '🎁'}</span>
                    <div className="font-semibold text-slate-800">
                      {program.program_name || program.company_name}
                    </div>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-slate-100 text-slate-700">
                      {programTypeLabels[program.program_type] || 'Other'}
                    </span>
                    {program.tier_level && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                        {program.tier_level}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditProgram(program, index)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRemoveProgram(index)}
                      className="p-1 text-red-500 hover:text-red-700"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Program Details */}
                <div className="text-sm">
                  <span className="text-slate-600">Member #:</span>
                  <span className="font-medium ml-2">{program.membership_number || program.member_number}</span>
                </div>

                {/* Notes */}
                {program.notes && (
                  <div className="text-sm text-slate-600 mt-2 italic">
                    {program.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {frequentTravelerPrograms.length === 0 && !showForm && (
          <div className="text-center py-8 text-slate-500">
            <svg className="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
            <p>No loyalty programs added yet</p>
          </div>
        )}

        {/* Add Program Button */}
        {!showForm && (
          <button
            onClick={() => {
              setShowForm(true);
              setError('');
            }}
            className="w-full p-4 border-2 border-dashed border-violet-300 rounded-xl hover:border-violet-400 hover:bg-violet-50 transition-colors"
          >
            <div className="flex items-center justify-center gap-2 text-violet-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="font-medium">Add Loyalty Program</span>
            </div>
          </button>
        )}

        {/* Add/Edit Program Form */}
        {showForm && (
          <div className="p-4 bg-violet-50 rounded-xl border border-violet-200">
            <h4 className="font-semibold text-slate-800 mb-4">
              {editingIndex !== null ? 'Edit' : 'Add'} Loyalty Program
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Program Type *</label>
                <select
                  value={currentProgram.program_type}
                  onChange={(e) => setCurrentProgram({...currentProgram, program_type: e.target.value})}
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg"
                >
                  <option value="airline">Airline</option>
                  <option value="hotel">Hotel</option>
                  <option value="rental_car">Rental Car</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Program Name *</label>
                <input
                  value={currentProgram.program_name}
                  onChange={(e) => setCurrentProgram({...currentProgram, program_name: e.target.value})}
                  placeholder="e.g., Delta SkyMiles, Marriott Bonvoy"
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Membership Number *</label>
                <input
                  value={currentProgram.membership_number}
                  onChange={(e) => setCurrentProgram({...currentProgram, membership_number: e.target.value})}
                  placeholder="e.g., 1234567890"
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Tier Level</label>
                <input
                  value={currentProgram.tier_level}
                  onChange={(e) => setCurrentProgram({...currentProgram, tier_level: e.target.value})}
                  placeholder="e.g., Gold, Platinum, Diamond"
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Notes</label>
                <textarea
                  value={currentProgram.notes}
                  onChange={(e) => setCurrentProgram({...currentProgram, notes: e.target.value})}
                  placeholder="Any additional notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProgram}
                className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg"
              >
                {editingIndex !== null ? 'Save Changes' : 'Add Program'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FrequentTravelerTab;
